import React, { useEffect, useRef, useState } from 'react';
import { Button, Flex, Text, Image } from 'rebass';
import { useParams } from 'react-router-dom';
import useEntity from 'entities/hooks/use-entity';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { get, getSWR } from 'utils/api';
import useSWR from 'swr';
import DirectorKitten from './assets/DirectorKitten.png'
import Modal from 'app/modal/v3/index';
import { useMobileCheckHook } from 'story-media/hooks';
import TextLayer from 'app/payment/TextLayer';
import NewImageLayer from 'collections/book-creation/components/SliderComponents/NewImageLayer';
import Loading from 'app/loading/index';
import useRouteFromEntityParams from 'collections/hooks/route-from-entity-params';
import useHistory from 'app/hooks/history';
import Wrapper from './Wrapper';

const InlineEditing = React.forwardRef(({ bookStyleMonth, posterSectionWidth, parentHeight, allWMGLayers, photo }, ref) => {
  const isFromMobile = useMobileCheckHook()
  const reactpinchzoomRef = useRef()
  const [zoom, setZoom] = useState(null);

  // useEffect(() => {
  //   if (reactpinchzoomRef.current) {
  //     const { zoomToElement } = reactpinchzoomRef.current;
  //     zoomToElement('templateContainer');
  //   }
  // }, [reactpinchzoomRef?.current]);

  useEffect(() => {
    if (posterSectionWidth && bookStyleMonth?.width) {
      const widthZoom = (posterSectionWidth / bookStyleMonth.width) * 100;
      const heightZoom = (parentHeight / bookStyleMonth.height) * 100;

      const zoomPercentage = Math.min(widthZoom, heightZoom);
      setZoom(zoomPercentage - 2);
    }
  }, [
    isFromMobile,
    posterSectionWidth,
    parentHeight,
    bookStyleMonth?.width,
    bookStyleMonth?.height,
  ]);

  return <TransformWrapper
    ref={reactpinchzoomRef}
    centerZoomedOut
    centerOnInit
    limitToBounds
  // disabled={!isTouchDevice()}
  >
    <TransformComponent contentStyle={{ width: '100%' }} wrapperStyle={{ width: '100%', padding: '10px' }}>
      <Flex
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        pt={4}
        ref={ref}
        sx={{
          zoom: `${zoom}% `,
          MozTransform: `scale(${zoom / 100})`,
          MozTransformOrigin: '10% 0',
          position: 'relative',
          boxShadow: '0px 7px 36px 5px rgba(0, 0, 0, 0.25)',
          width: `${bookStyleMonth?.width}px`,
          height: `${bookStyleMonth?.height}px`,
          left: 0,
          right: 0,
          m: 'auto',
          touchAction: 'pan-x pan-y pinch-zoom',
          bg: bookStyleMonth?.['background-colour']
        }}
        id="templateContainer"
      >
        {/* {bookStyleMonth?.height && <Box sx={{ zIndex: 9999 }}>
          <WaterMark height={bookStyleMonth?.height} width={bookStyleMonth?.width} />
        </Box>} */}
        {allWMGLayers && allWMGLayers.map(({ attributes, relationships }) => attributes?.type === 'Books::WatchMeGrowOnePagerTextLayer' ? <TextLayer
          {...attributes}
          zoom={zoom}
          xpos={attributes?.xpos}
          ypos={attributes?.ypos}
          fontAlignment={attributes?.['font-alignment']}
          fontFamily={attributes?.['font-name']}
          fontWeight={attributes?.['font-weight']}
          fontSize={attributes?.['font-size']}
          color={attributes?.['font-colour']}
        /> : <NewImageLayer
          {...attributes}
          {...{
            // NewImageLayer positions via relative-xpos/ypos; the collage layers use
            // xpos/ypos — map them so placement matches the previous ImageLayer.
            'relative-xpos': attributes?.['relative-xpos'] ?? attributes?.xpos,
            'relative-ypos': attributes?.['relative-ypos'] ?? attributes?.ypos,
          }}
          // Required by NewImageLayer (it does pageName.includes(...) unguarded) and
          // selects its WatchMeGrow crop-rendering path.
          pageName={attributes?.pageName || attributes?.['page-name'] || 'WMG'}
          photo={relationships?.['canvas-image']?.data || relationships?.['photo']?.data || relationships?.['source-media']?.data}
          zoom={zoom}
        />)}
      </Flex>
    </TransformComponent>
  </TransformWrapper>
})

const CollagePreview = () => {
  const [processVideo, setIsProcessing] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [parentHeight, setParentHeight] = useState(null);
  const [posterSectionWidth, setPosterSectionWidth] = useState(0);
  const ref = useRef();
  const { replace } = useHistory();

  const params = useParams();
  const route = useRouteFromEntityParams(params);

  const { entity } = useEntity(params);
  const firstBookId = entity?.books?.[0]?.id || '';
  const { data } = useSWR(firstBookId ? `/v3/books/${firstBookId}` : null, getSWR, { refreshInterval });
  const videoId = data?.data?.data?.attributes?.['watch-me-grow-video-id'];

  const { data: videoData } = useSWR(firstBookId && videoId ? `/v3/books/${firstBookId}/watch_me_grow_videos/${videoId}` : null);
  const processingState = videoData?.data?.data?.attributes?.state;

  const isVideoGenerated = processingState === 'finished' || processingState === 'completed'
  const uploadStatus = data?.data?.data?.attributes?.['pdf-is-being-created'];
  const allowPDFGeneration = data?.data?.data?.attributes?.['allow-generation-of-watch-me-grow-collage']

  const reasonToNotGenerateWMGC = data?.data?.data?.attributes?.['reason-to-not-generate-watch-me-grow-collage']

  const allWMGLayers = data?.data?.included.filter(({ type }) => type === 'watch-me-grow-one-pager-layers')
  const bookStyleMonth = data?.data?.included.find(bookDetail => {
    const onePagerDeets = data?.data?.data?.relationships?.['watch-me-grow-one-pager']?.data
    return bookDetail?.id === onePagerDeets?.id && bookDetail?.type === onePagerDeets?.type
  })

  const thumbnailDetails = data?.data?.included.find(bookDetail => {
    const thumbnailDeets = data?.data?.data?.relationships?.['watch-me-grow-one-pager-pdf-thumbnail']?.data
    return bookDetail?.id === thumbnailDeets?.id && bookDetail?.type === thumbnailDeets?.type
  })

  useEffect(() => {
    // if (uploadStatus) {
    if (refreshInterval !== null && !uploadStatus) {
      setRefreshInterval(0);
      setIsLoading(false)
      setIsProcessing(false)
    } else {
      setIsLoading(true)
      setRefreshInterval(5000);
    }
    // }
  }, [!!uploadStatus, setRefreshInterval]);

  const patchWatchMeGrow = async () => {
    await getSWR(`/v3/books/${firstBookId}/watch_me_grow.pdf`);
    setIsLoading(true)
    setRefreshInterval(5000)
  }

  const onClose = () => {
    setIsProcessing(false)
  }

  function handleSubTitle() {
    if (allowPDFGeneration) return 'Approve the preview to generate your printable collage.'
    else if (isLoading) return 'Creating your printable collage PDF! This may take a few minutes.'
    else return 'All set! Your Watch Me Grow collage is ready to download and print.'
  }

  return (
    <Wrapper >
      {(processVideo) && <Modal
        maxWidth={500}
        shadeProps={{ pt: [0, 4] }}
        closeButton
        close={{ onClose }}
        childSx={{ marginTop: '-40px', zIndex: 1 }}
        // aboveTooltip
        closeOnOutsideClick
      >
        <Flex justifyContent="center" alignItems="center" flexDirection="column" px={2}>
          <Text
            color="#000"
            fontFamily="Poppins"
            fontSize={"22px"}
            lineHeight="165%"
            mb={3}
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
              display: 'flex',
              gap: '10px'
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
              <path d="M10.5 13.3327V9.99935M10.5 6.66602H10.5083M18.8333 9.99935C18.8333 14.6017 15.1023 18.3327 10.5 18.3327C5.89759 18.3327 2.16663 14.6017 2.16663 9.99935C2.16663 5.39698 5.89759 1.66602 10.5 1.66602C15.1023 1.66602 18.8333 5.39698 18.8333 9.99935Z" stroke="#1E1E1E" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
            </svg>  {isVideoGenerated ? 'Video Updated?' : 'Processing Required'}
          </Text>
          <Text textAlign="center" fontFamily="Poppins" fontSize={16} fontWeight={500} letterSpacing="0.16px" lineHeight="165%" color="#64748B" >
            {isVideoGenerated ? 'Have your latest changes been processed in your video?' : 'Watch and process your video to create your QR code'}
          </Text>
          <Image
            src={DirectorKitten}
            maxWidth={300}
          />
          <Flex pb={2} sx={{ gap: "10px" }} flexDirection="column" alignItems="center" justifyContent="center">
            {isVideoGenerated ?
              <a
                href={`${thumbnailDetails?.attributes?.['asset-url']}?download=true`}
                download
              >
                <Button sx={{ borderRadius: '23px', padding: '10px 20px', height: '44px' }} >
                  Yes, Download Collage
                </Button>
              </a>
              : <Button sx={{ borderRadius: '23px', padding: '10px 20px', height: '44px' }} onClick={() => {
                replace(`${route}/video-preview`)
              }}>
                Preview Video
              </Button>}
            {isVideoGenerated ?
              <Button onClick={() => {
                replace(`${route}/video-preview`)
              }} color="#9B59B6" sx={{ borderRadius: '23px', border: '1.5px solid #9B59B6', padding: '10px 20px', height: '44px', bg: 'transparent' }} >
                No, Preview Video
              </Button>
              : <Button onClick={onClose} sx={{ borderRadius: '23px', padding: '10px 20px', height: '44px', bg: '#BDBDBD', border: 0 }} >
                Cancel
              </Button>}
          </Flex>
        </Flex>
      </Modal>}
      {/* <Flex justifyContent={'center'} alignItems={'center'}>
        <Text fontFamily="Bitter" fontSize={30} letterSpacing="-0.66px" lineHeight="120%">
          Book Preview
        </Text>
      </Flex> */}

      <Flex justifyContent="center" alignItems="center" flexDirection="column" my={2} >
        <Flex justifyContent={'center'} alignItems={'center'}>
          <Text fontFamily="Bitter" fontSize={30} letterSpacing="-0.66px" lineHeight="120%">
            PDF Download Preview
          </Text>
        </Flex>
        {allowPDFGeneration !== undefined && <Text color="#64748B" textAlign="center" fontWeight={500} my={2} fontFamily="Poppins" fontSize="16px" letterSpacing="0.16px" lineHeight="165%">
          {handleSubTitle()}
        </Text>}
        {allowPDFGeneration !== undefined && <>
          {isLoading ?
            <Loading local />
            : <>
              {(allowPDFGeneration && !reasonToNotGenerateWMGC) ?
                <Button m="auto" color={"#9B59B6"} bg={"transparent"} height="44px" sx={{
                  borderRadius: '23px',
                  border: '1.5px solid #9B59B6'
                }} p="0px 20px" onClick={patchWatchMeGrow}>
                  Approve Preview
                </Button>
                :
                <Button m="auto" color={'white'} bg={'#9B59B6'} height="44px" sx={{
                  borderRadius: '23px',
                  border: 'unset'
                }} p="0px 20px" onClick={() => setIsProcessing(true)}>
                  Download
                </Button>}
            </>
          }
        </>}
      </Flex>

      {allowPDFGeneration === undefined ?
        <Flex
          m="auto"
          width="100%"
          // height="100%"
          mt={5}
          maxWidth={700}
          bg="#E0EFFF"
          height={["calc(100vh - 400px)", "calc(100vh - 300px)"]}
          overflow="auto"
          flexDirection="column"
          justifyContent="center"
          alignItem="center"
          sx={{
            position: 'relative', borderRadius: '8px',
            border: '1px solid rgba(0, 0, 0, 0.05)',
          }}
        >
          <Text color="#0E0E0E" fontWeight={400} mb={3} textAlign="center" fontFamily="Poppins" fontSize="18px" letterSpacing="0.16px" lineHeight="36px">
            Please wait a moment as we refresh your preview
          </Text>
          <Loading local />
        </Flex>
        : <Flex justifyContent="center" alignItems="center">
          {/* {uploadStatus === 'finished' ? */}
          <Flex
            width="100%"
            // height="100%"
            maxWidth={700}
            height={["calc(100vh - 400px)", "calc(100vh - 300px)"]}
            overflow="auto"
            justifyContent="center"
            alignItem="center"
            sx={{
              position: 'relative', borderRadius: '8px',
              border: '1px solid rgba(0, 0, 0, 0.05)',
            }}
            ref={(el) => {
              if (!el) return;
              setPosterSectionWidth(el.getBoundingClientRect().width);
              setParentHeight(el.getBoundingClientRect().height);
            }}
          >
            {/* {allWMGLayers && allWMGLayers.map(({ attributes, relationships }) => {
            return  */}
            <InlineEditing
              allWMGLayers={allWMGLayers}
              parentHeight={parentHeight}
              posterSectionWidth={posterSectionWidth}
              bookStyleMonth={bookStyleMonth?.attributes}
              ref={ref}
            />
            {/* })} */}
            {/* // <Image
          //   maxWidth={500}
          //   sx={{ borderRadius: '12px' }}
          //   src={thumbnailDetails?.attributes?.['asset-url']}
          // /> */}
          </Flex>
          {/* :
          <LoadingBg sx={{ gridArea: '1 / 1 / 2 / 2' }} maxWidth={500} minWidth={300} height={500} >
            <Text className="loading" color="white" textAlign="center" mt="50%">
              Processing
            </Text>
          </LoadingBg>
        } */}
        </Flex>}
    </Wrapper>
  );
};

export default CollagePreview;